<script lang="ts">
import StatsBanner from '@/components/banners/StatsBanner.vue'
import QuickLinksCard from '@/components/cards/QuickLinksCard.vue'
import BannerLayoutProvider from '@/components/sections/Banner/BannerLayoutProvider.vue'
import GamesSection from '@/components/sections/Homepage/GamesSection.vue'
import MembersPrizesSection from '@/components/sections/Homepage/MembersPrizesSection.vue'
import LatestWinnersSection from '@/components/sections/LatestWinnersSection.vue'
import ProductOverviewSection from '@/components/sections/ProductOverviewSection.vue'
import SponsorshipSection from '@/components/sections/SponsorshipSection.vue'
import WinnersSection from '@/components/sections/WinnersSection.vue'
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { useTailwind } from '@/plugins/useTailwind'
import { useFaqStore } from '@/stores/faq'
import type { Campaign, Nullable } from '@/types'
import { Themes } from '@/utils/helper-objects'
import FaqView from '@/views/faq/FaqView.vue'
import { defineComponent } from 'vue'

/**
 * The "Home" page.
 */
export default defineComponent({
  components: {
    BannerLayoutProvider,
    FaqView,
    GamesSection,
    LatestWinnersSection,
    MembersPrizesSection,
    ProductOverviewSection,
    QuickLinksCard,
    SponsorshipSection,
    StatsBanner,
    WinnersSection,
  },
  setup() {
    const { breakpoints } = useTailwind()

    return {
      breakpoints,
      routeMeta: useRouteMeta(),
      faqStore: useFaqStore(),
      Themes,
    }
  },
  data() {
    return {
      columnsPerRow: 4,
      resizeObserver: null as ResizeObserver | null,
    }
  },
  computed: {
    featuredCampaign(): Nullable<Campaign> {
      return this.routeMeta.meta?.featuredCampaign ?? null
    },
  },
  mounted() {
    this.setupResizeObserver()
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect()
  },
  methods: {
    setupResizeObserver() {
      const mainContainer = document.querySelector('main')
      if (!mainContainer) {
        return
      }

      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width
        if (width >= this.breakpoints.lg) {
          this.columnsPerRow = 4
        } else if (width >= this.breakpoints.md) {
          this.columnsPerRow = 3
        } else {
          this.columnsPerRow = 2
        }
      })
      this.resizeObserver.observe(mainContainer)
    },
  },
})
</script>

<template>
  <BannerLayoutProvider />

  <QuickLinksCard />

  <LatestWinnersSection />

  <StatsBanner />

  <!-- Instant Prizes -->
  <GamesSection :columns-per-row="columnsPerRow" />

  <!-- Members Prizes -->
  <MembersPrizesSection :columns-per-row="columnsPerRow" />

  <SectionGuard section="as-seen-in">
    <AsSeenInBanner />
  </SectionGuard>

  <SectionGuard section="benefits">
    <ProductOverviewSection id="product-overview-section" />
  </SectionGuard>

  <PlansSection />

  <FaqView :show-breadcrumb="false" />

  <WinnersSection :title="$t('section.winners.heading')">
    <template #sub-heading>
      {{ $t('section.winners.excerpt') }}
    </template>
  </WinnersSection>

  <SponsorshipSection :display-watch-button="false" />
</template>
